import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "sonner";
import axios from "axios";
import AnnouncementBar from "@/components/AnnouncementBar";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AuthCallback from "@/components/auth/AuthCallback";
import Footer from "@/components/Footer";
import Header from "@/components/layout/Header";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { usePostRegisterInvite } from "@/hooks/usePostRegisterInvite";
import Analyze from "@/pages/Analyze";
import Dashboard from "@/pages/Dashboard";
import Discover from "@/pages/Discover";
import EmailVerification from "@/pages/EmailVerification";
import ForgotPassword from "@/pages/ForgotPassword";
import History from "@/pages/History";
import Login from "@/pages/Login";
import PaymentHistory from "@/pages/PaymentHistory";
import PaymentFinish from "@/pages/PaymentFinish";
import PaymentUnfinish from "@/pages/PaymentUnfinish";
import PaymentError from "@/pages/PaymentError";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import Register from "@/pages/Register";
import ResendVerification from "@/pages/ResendVerification";
import Results from "@/pages/Results";
import SharedResult from "@/pages/SharedResult";
import Subscription from "@/pages/Subscription";
import UserAnalytics from "@/pages/UserAnalytics";
import Videos from "@/pages/Videos";
import Maintenance from "@/pages/Maintenance";
import AdminAnalyses from "@/pages/admin/AdminAnalyses";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminDevEmails from "@/pages/admin/AdminDevEmails";
import AdminEmailBlast from "@/pages/admin/AdminEmailBlast";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminUsers from "@/pages/admin/AdminUsers";
import { setupMaintenanceRedirectInterceptor } from "@/utils/axiosMaintenanceInterceptor";

setupMaintenanceRedirectInterceptor();
const API = `${process.env.REACT_APP_API_URL}/api`;

function InviteAutoRedeemBridge() {
  usePostRegisterInvite();
  return null;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-background" data-testid="app-root-loading" />;
  }
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function App() {
  const [maintenanceGateLoading, setMaintenanceGateLoading] = useState(true);
  const [maintenanceBlocked, setMaintenanceBlocked] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkMaintenanceAccess = async () => {
      try {
        const res = await axios.get(`${API}/system/status`, { withCredentials: true });
        if (!mounted) return;
        const maintenance = Boolean(res?.data?.maintenance);
        const accessAllowed = res?.data?.access_allowed !== false;
        setMaintenanceBlocked(maintenance && !accessAllowed);
      } catch {
        if (!mounted) return;
        setMaintenanceBlocked(false);
      } finally {
        if (mounted) setMaintenanceGateLoading(false);
      }
    };

    checkMaintenanceAccess();
    return () => {
      mounted = false;
    };
  }, []);

  if (maintenanceGateLoading) {
    return (
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <div className="App min-h-screen bg-background text-foreground">
          <div className="noise-overlay" aria-hidden="true" />
        </div>
      </ThemeProvider>
    );
  }

  if (maintenanceBlocked) {
    return (
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <div className="App min-h-screen bg-background text-foreground">
          <div className="noise-overlay" aria-hidden="true" />
          <BrowserRouter>
            <Routes>
              <Route path="*" element={<Maintenance />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <div className="App min-h-screen bg-background text-foreground">
        <div className="noise-overlay" aria-hidden="true" />
        <BrowserRouter>
          <AuthProvider>
            <InviteAutoRedeemBridge />
            <div className="sticky top-0 z-50 flex flex-col">
              <AnnouncementBar />
              <Header />
            </div>
            <Routes>
              <Route path="/" element={<RootRedirect />} />

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
              <Route path="/payment/finish" element={<PaymentFinish />} />
              <Route path="/payment/unfinish" element={<PaymentUnfinish />} />
              <Route path="/payment/error" element={<PaymentError />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/analyze" element={<ProtectedRoute><Analyze /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
              <Route path="/results/:id" element={<ProtectedRoute><Results /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
              <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
              <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
              <Route path="/videos" element={<ProtectedRoute><Videos /></ProtectedRoute>} />
              <Route path="/shared/:id" element={<ProtectedRoute><SharedResult /></ProtectedRoute>} />
              <Route path="/share/:id" element={<ProtectedRoute><SharedResult /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><UserAnalytics /></ProtectedRoute>} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="analyses" element={<AdminAnalyses />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="finance" element={<AdminFinance />} />
                <Route path="transactions" element={<AdminTransactions />} />
                <Route path="packages" element={<AdminPackages />} />
                <Route path="email-blast" element={<AdminEmailBlast />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="dev-emails" element={<AdminDevEmails />} />
              </Route>

              <Route path="*" element={<RootRedirect />} />
            </Routes>
            <Footer />
          </AuthProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;

